import React, { useState, useEffect, useMemo } from 'react';
import classnames from 'classnames';
import { useSelector } from 'react-redux';

import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import ErrorWrapper from 'dpl/form/components/ErrorWrapper';
import DividerWithText from 'dpl/auth/components/DividerWithText';
import ContinueWithGoogleButton from 'dpl/auth/components/ContinueWithGoogleButton';
import useForm from 'dpl/shared/form/hooks/useForm';
import { CONSTRAINT_TYPES } from 'dpl/shared/validations/constraints';
import { SIGNUP_FORM_TYPES } from 'dpl/shared/constants/shared';
import { PRIVACY_PATH, TOS_PATH } from 'dpl/shared/constants/urls';
import { sendAnalyticsEvent } from 'dpl/util/analytics';
import { ANALYTICS_EVENTS } from 'dpl/constants/analytics';
import encodeState from 'dpl/auth/utils/encodeState';
import buildAuthRedirectUrl from 'dpl/auth/utils/buildAuthRedirectUrl';
import Input from 'dpl/common/design_system/Input';

import { emailAvailabilityQueryDefinition } from './queryDefinitions';
import ContinueWithAppleButton from '../ContinueWithAppleButton';

interface EmailVerificationFormProps {
  onEmailVerified: (email: string) => void;
  oauthState: {
    redirect_to?: string;
    user_type: string;
  };
  formType?: string;
  headerTitle?: string | null;
  analytics: {
    view?: string;
  };
}

export default function EmailVerificationForm({
  onEmailVerified,
  oauthState,
  formType = SIGNUP_FORM_TYPES.LOGIN,
  headerTitle = null,
  analytics
}: EmailVerificationFormProps) {
  const oauthStateSafeBase64 = useMemo(() => {
    return encodeState({ ...oauthState, redirect_to: buildAuthRedirectUrl() });
  }, [oauthState]);
  const oauthError = useSelector((state: any) => state.queryParams.oauth_error);
  const [shouldCheckEmail, setShouldCheckEmail] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const continueWithEmailText = 'Continue with email';

  const {
    formState,
    handleFieldChange,
    formErrors,
    didSubmissionFail,
    handleSubmission
  } = useForm({
    defaultFormState: { email: '' },
    mapFormStateToValidationSchema: () => ({
      email: CONSTRAINT_TYPES.EMAIL
    }),
    onSubmit: async (fs: any) => {
      setShouldCheckEmail(true);
      return fs;
    }
  });

  const { email } = formState;

  const { value: emailAvailabilityData, isFetching: isCheckingEmail } =
    useResourceQuery({
      ...emailAvailabilityQueryDefinition(email),
      enabled: shouldCheckEmail && Boolean(email)
    });

  useEffect(() => {
    if (shouldCheckEmail && !isCheckingEmail) {
      if (
        formType === SIGNUP_FORM_TYPES.LOGIN &&
        emailAvailabilityData.status !== 'exists'
      ) {
        setEmailError('This email is not registered. Please sign up instead.');
      } else if (
        formType === SIGNUP_FORM_TYPES.SIGNUP &&
        emailAvailabilityData.status === 'exists'
      ) {
        setEmailError('This email is registered. Please sign in instead.');
      } else {
        setEmailError(null);
        onEmailVerified(email);
      }
      setShouldCheckEmail(false);
    }
  }, [
    emailAvailabilityData,
    shouldCheckEmail,
    email,
    isCheckingEmail,
    onEmailVerified
  ]);

  async function handleEmailFormSubmit(e: React.FormEvent) {
    e.preventDefault();
    sendAnalyticsEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      ...analytics,
      button_cta: continueWithEmailText
    });
    if (email) {
      setEmailError(null);
      await handleSubmission();
    } else {
      setEmailError("Email can't be blank");
    }
  }

  useEffect(() => {
    sendAnalyticsEvent(ANALYTICS_EVENTS.REGISTRATION_MODAL_VIEWED, analytics);
  }, []);

  return (
    /* eslint-disable jsx-a11y/tabindex-no-positive */
    <form onSubmit={handleEmailFormSubmit} className="EmailVerificationForm">
      {headerTitle && (
        <h1 className="md:font-32 font-28 fw-medium tc lh-title pb8">
          {headerTitle}
        </h1>
      )}
      <div className="row mb3">
        <div className="col">
          <label htmlFor="email">
            <ErrorWrapper
              errors={emailError ? [emailError] : formErrors.email}
              isShown={!!emailError || didSubmissionFail}
            >
              <Input
                tabIndex={1}
                name="email"
                className="w-100"
                value={email}
                onChange={handleFieldChange}
                placeholder="Email"
                autoComplete="username"
              />
            </ErrorWrapper>
          </label>
        </div>
      </div>
      <div>
        <button
          data-test-id="email_verification_submit"
          type="submit"
          disabled={isCheckingEmail}
          className={classnames(
            'font-16 w-100 fw-medium pv3 button button--primary',
            {
              'button--loading': isCheckingEmail
            }
          )}
        >
          {isCheckingEmail ? 'Checking...' : continueWithEmailText}
        </button>
        <DividerWithText text="or" />
        <div className="mb2">
          <ContinueWithGoogleButton
            state={oauthStateSafeBase64}
            analytics={analytics}
          />
        </div>
        {formType === SIGNUP_FORM_TYPES.LOGIN && (
          <ContinueWithAppleButton
            state={oauthStateSafeBase64}
            analytics={analytics}
          />
        )}
        {oauthError && <p className="ma1 pt4 red tc font-14">{oauthError}</p>}
        {formType === SIGNUP_FORM_TYPES.SIGNUP && (
          <div className="mb6 mt4">
            <p className="f2 mid-gray tc">
              By continuing, I agree to Good Dog&apos;s&nbsp;
              <a
                className="underline"
                href={TOS_PATH}
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Service
              </a>
              &nbsp;and{' '}
              <a
                className="underline"
                href={PRIVACY_PATH}
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
              .
            </p>
          </div>
        )}
      </div>
    </form>
  );
  /* eslint-enable jsx-a11y/tabindex-no-positive */
}
